/**
 * @-mentions of creators the Account can tag (OnlyFans tagged-friend-users),
 * as the CRM chat window's chat-composer-tools.js does: first page on open,
 * OnlyFans' own `name` search from two characters.
 */
import { crmFetch, dataOf } from './api';

export interface Creator {
  id: string;
  username: string;
  name: string;
}

type Raw = Record<string, unknown>;

function listOf(body: unknown): Raw[] {
  const d = (dataOf<Raw>(body) ?? {}) as Raw;
  const inner = (d.data && typeof d.data === 'object' ? d.data : {}) as Raw;
  const hit = [d.list, d.users, d.items, d.friends, inner.list, inner.users, inner.items, Array.isArray(d) ? d : null].find(Array.isArray);
  return (hit || []) as Raw[];
}

export function creatorOf(item: Raw): Creator {
  // The canonical copy is nested in `.user`; the outer item repeats some fields.
  const u = item.user && typeof item.user === 'object' ? { ...item, ...(item.user as Raw) } : item;
  const id = String(u.id ?? u.user_id ?? u.userId ?? '').trim();
  const username = String(u.username ?? u.nameUsername ?? u.handle ?? u.nickname ?? '')
    .trim()
    .replace(/^@+/, '');
  const name = String(u.name ?? u.displayName ?? u.fullName ?? username).trim();
  return { id, username, name: name || username || id };
}

const cache = new Map<string, Promise<Creator[]>>();

export function searchCreators(accountId: string, query: string): Promise<Creator[]> {
  const q = query.trim().toLowerCase();
  const key = `${accountId}:${q}`;
  let hit = cache.get(key);
  if (!hit) {
    hit = crmFetch('/v1/posts/tagged-friend-users', {
      accountId,
      body: { limit: 50, offset: 0, skip_users: 'all', filter: 'all', sort: 'date:desc', name: q || null },
    })
      .then((body) => listOf(body).map(creatorOf).filter((c) => c.username))
      .catch((err) => {
        cache.delete(key);
        throw err;
      });
    cache.set(key, hit);
  }
  return hit;
}

/** The "@query" the caret sits in, or null. */
export function mentionAt(value: string, caret: number): { start: number; query: string } | null {
  const m = value.slice(0, caret).match(/(^|\s)@([\w.-]{0,30})$/);
  return m ? { start: caret - m[2]!.length - 1, query: m[2]!.toLowerCase() } : null;
}

/** Creators matching `query`, the searched page first, at most `max`. */
export function rankCreators(query: string, pools: Creator[][], max = 8): Creator[] {
  const seen = new Map<string, Creator>();
  for (const c of pools.flat()) if (!seen.has(c.username.toLowerCase())) seen.set(c.username.toLowerCase(), c);
  return [...seen.values()].filter((c) => !query || c.username.toLowerCase().includes(query) || c.name.toLowerCase().includes(query)).slice(0, max);
}
